import React, { useEffect, useRef, useState } from "react";
import { api, API, currentToken } from "@/lib/api";
import { toast } from "sonner";
import { Upload, File as FileIcon, Trash2, Eye, ImageIcon, Loader2 } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,application/pdf";

/**
 * Reusable uploader + list.
 * Props:
 *  - kind: "patient_document" | "doctor_verification_id" | "doctor_verification_registration" | ...
 *  - title: string
 *  - description: string
 *  - showKindPicker: bool (for doctor verification lets user pick doc type)
 *  - kindOptions: [[value,label]]
 */
export default function DocumentUploader({ kind = "patient_document", title, description, showKindPicker = false, kindOptions = [] }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const [selectedKind, setSelectedKind] = useState(kindOptions[0]?.[0] || kind);
  const inputRef = useRef(null);

  const filterPrefix = showKindPicker ? "doctor_verification_" : kind;

  const load = async () => {
    const { data } = await api.get("/documents/mine");
    if (showKindPicker) {
      setDocs(data.filter(d => (d.kind || "").startsWith("doctor_verification_")));
    } else {
      setDocs(data.filter(d => d.kind === kind));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", showKindPicker ? selectedKind : kind);
      if (note) fd.append("note", note);
      const res = await fetch(`${API}/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${currentToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(()=>({}));
        throw new Error(j.detail || "Upload failed");
      }
      toast.success("Uploaded");
      setNote("");
      await load();
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const view = async (d) => {
    try {
      const res = await fetch(`${API}/documents/${d.id}/download`, {
        headers: { Authorization: `Bearer ${currentToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Could not open"); }
  };

  const labelFor = (k) => (kindOptions.find(([v]) => v === k)?.[1]) || k.replace("doctor_verification_", "").replace("_", " ");

  return (
    <div className="cm-card space-y-4" data-testid={`uploader-${filterPrefix}`}>
      <div>
        <h3 className="font-semibold text-[#1a2b4a]">{title}</h3>
        {description && <p className="text-sm text-[#475569] mt-1">{description}</p>}
      </div>

      <div className="rounded-lg border-2 border-dashed border-[#e2e8f0] bg-[#f5f7fa] p-6 text-center">
        {showKindPicker && (
          <div className="mb-3 flex justify-center">
            <select className="cm-input max-w-xs" value={selectedKind} onChange={e=>setSelectedKind(e.target.value)} data-testid="uploader-kind">
              {kindOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}
        <div className="w-12 h-12 rounded-full bg-[#ebf3fc] text-[#4a90d9] flex items-center justify-center mx-auto mb-3">
          {uploading ? <Loader2 className="animate-spin" size={22}/> : <Upload size={22}/>}
        </div>
        <div className="text-sm text-[#1a2b4a] font-semibold">Drop a file here, or</div>
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="uploader-input"/>
        <button className="cm-btn-primary mt-3" onClick={()=>inputRef.current?.click()} disabled={uploading} data-testid="uploader-choose">
          {uploading ? "Uploading…" : "Choose file"}
        </button>
        <div className="text-xs text-[#94a3b8] mt-2">JPG, PNG, WEBP, GIF or PDF · up to 10 MB</div>
        <input className="cm-input mt-3" placeholder="Add a note (optional)" value={note} onChange={e=>setNote(e.target.value)} data-testid="uploader-note"/>
      </div>

      <div className="divide-y divide-[#e2e8f0]" data-testid="uploader-list">
        {docs.length === 0 && <div className="text-sm text-[#475569] py-2">No documents uploaded yet.</div>}
        {docs.map(d => (
          <div key={d.id} className="py-3 flex items-center gap-3" data-testid={`uploader-item-${d.id}`}>
            <div className="w-10 h-10 rounded-lg bg-[#ebf3fc] text-[#4a90d9] flex items-center justify-center flex-shrink-0">
              {d.content_type?.startsWith("image/") ? <ImageIcon size={18}/> : <FileIcon size={18}/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#1a2b4a] truncate">{d.filename}</div>
              <div className="text-xs text-[#475569]">
                {showKindPicker && <span className="mr-2 capitalize">{labelFor(d.kind)}</span>}
                {(d.size/1024).toFixed(0)} KB · {(d.created_at||"").slice(0,10)}
              </div>
              {d.note && <div className="text-xs text-[#94a3b8] italic mt-0.5 truncate">"{d.note}"</div>}
            </div>
            <button className="cm-btn-outline text-xs py-1.5 px-2.5" onClick={()=>view(d)} data-testid={`uploader-view-${d.id}`}><Eye size={14}/></button>
            <button className="cm-btn-outline text-xs py-1.5 px-2.5" onClick={()=>remove(d.id)} data-testid={`uploader-delete-${d.id}`}><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
